import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, UseFormReturn, useWatch } from "react-hook-form";
import { Container } from "react-bootstrap";
import { useRouteContext } from "@tanstack/react-router";
import { SCLR } from "@/models";
import { FiUsers } from "react-icons/fi";
import { FaTrashAlt, FaChevronDown, FaChevronUp } from "react-icons/fa";
import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import TextInput from "@/components/shared/forms/TextInput";
import Select from "@/components/shared/forms/Select";
import SearchComboBox, {
  type ComboBoxOption,
} from "@/components/shared/forms/SearchComboBox";
import { useCoreData, useLeaData } from "@/queries.ts";

type SCLRWizardFormProps = {
  form: UseFormReturn<SCLR, any, SCLR>;
  classroomNumber?: number;
  onRemove?: () => void;
  allowedPreschoolCodes?: string[] | null;
};

export function SCLRWizardForm({
  form,
  classroomNumber = 1,
  onRemove,
  allowedPreschoolCodes = null,
}: SCLRWizardFormProps) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const viewOnly = !leaContext.hasRole("SCLREdit");
  const coreData = useCoreData();
  const tooltipMap = leaContext.coreData.tooltipMap.get("SCLR");

  const esiIdPath = leaContext.currentUserOrg?.esiIdPath ?? "";
  const leaDataResult = useLeaData(esiIdPath);
  const leaData = leaDataResult.data;

  const idPrefix = `classroom-${classroomNumber}-`;

  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = form;

  const programYear = useWatch({ control, name: "ProgramYear" });
  const preschoolCode = useWatch({ control, name: "PreschoolCode" });
  const prevPreschoolRef = useRef(preschoolCode);

  useEffect(() => {
    if (prevPreschoolRef.current !== preschoolCode) {
      prevPreschoolRef.current = preschoolCode;
      setValue("LocalClassroomId", "", { shouldValidate: false });
    }
  }, [preschoolCode, setValue]);

  useEffect(() => {
    if (
      allowedPreschoolCodes &&
      preschoolCode &&
      !allowedPreschoolCodes.includes(preschoolCode)
    ) {
      setValue("PreschoolCode", "", { shouldValidate: false });
    }
  }, [allowedPreschoolCodes, preschoolCode, setValue]);

  const allPreschoolOptions = leaContext
    .currentOrgPreschoolsByProgramYear(programYear)
    .map((x) => ({
      Text: x.PreschoolName,
      Value: x.PreschoolCDSCode,
    }));

  const preschoolOptions =
    allowedPreschoolCodes && allowedPreschoolCodes.length > 0
      ? allPreschoolOptions.filter((o) =>
          allowedPreschoolCodes.includes(o.Value)
        )
      : allPreschoolOptions;

  const classroomOptions = useMemo<ComboBoxOption[]>(() => {
    if (!leaData?.entities?.classrooms || !preschoolCode) return [];
    return Object.values(leaData.entities.classrooms)
      .map((c: any) => c.records[0])
      .filter(
        (clrm: any) =>
          clrm &&
          clrm.LocalClassroomId &&
          clrm.PreschoolCode === preschoolCode &&
          clrm.ProgramYear === programYear &&
          !clrm.EffectiveEndDate
      )
      .map((clrm: any) => ({
        value: clrm.LocalClassroomId,
        label: clrm.ClassroomName ?? "",
        sublabel: `ID: ${clrm.LocalClassroomId}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [leaData, preschoolCode, programYear]);

  const [collapsed, setCollapsed] = useState(false);

  const hasErrors = Object.keys(errors).length > 0;
  useEffect(() => {
    if (hasErrors) setCollapsed(false);
  }, [hasErrors]);

  const Wrapper = classroomNumber === 1 ? Container : "div";
  const wrapperProps =
    classroomNumber === 1
      ? {
          className: "form-style overview-container ps-3 pe-3",
          style: { borderTop: "none", borderRadius: 0 } as React.CSSProperties,
        }
      : {
          className: "ps-3 pe-3",
        };

  const headerStyle = {
    backgroundColor: "#F9FAFB",
    padding: "14px 20px",
    userSelect: "none" as const,
  };

  return (
    <Wrapper {...wrapperProps}>
      <div
        className="form-card-modern mb-3"
        style={{
          overflow: "hidden",
          padding: 0,
          ...(onRemove
            ? {
                border: "2px dashed #c5ccd4",
                borderRadius: "8px",
              }
            : {}),
        }}
      >
        <div
          className="section-header d-flex justify-content-between align-items-center"
          style={{
            ...headerStyle,
            width: "100%",
            ...(collapsed ? { marginBottom: 0 } : {}),
          }}
        >
          <h2 className="section-title mb-0 flex-grow-1">
            <button
              type="button"
              className="d-flex align-items-center gap-2 w-100"
              style={{
                border: "none",
                background: "none",
                font: "inherit",
                textAlign: "left" as const,
                padding: 0,
                cursor: "pointer",
              }}
              onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed}
            >
              <FiUsers className="section-icon" aria-hidden="true" />
              {classroomNumber === 1
                ? "Classroom Information"
                : `Classroom Information ${classroomNumber}`}
            </button>
          </h2>
          <div className="d-flex align-items-center gap-2">
            {onRemove && (
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-1"
                style={{
                  color: "#b02a37",
                  border: "none",
                  background: "transparent",
                  fontWeight: 500,
                  fontSize: "13px",
                  cursor: "pointer",
                }}
                onClick={onRemove}
                aria-label={
                  classroomNumber === 1
                    ? "Remove Classroom Information"
                    : `Remove Classroom Information ${classroomNumber}`
                }
              >
                <FaTrashAlt style={{ fontSize: "12px" }} />
                Remove
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm d-flex align-items-center"
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
              }}
              onClick={() => setCollapsed((c) => !c)}
              aria-expanded={!collapsed}
              aria-label={
                collapsed
                  ? classroomNumber === 1
                    ? "Expand Classroom Information"
                    : `Expand Classroom Information ${classroomNumber}`
                  : classroomNumber === 1
                    ? "Collapse Classroom Information"
                    : `Collapse Classroom Information ${classroomNumber}`
              }
            >
              {collapsed ? (
                <FaChevronDown style={{ fontSize: "12px", color: "#6c757d" }} />
              ) : (
                <FaChevronUp style={{ fontSize: "12px", color: "#6c757d" }} />
              )}
            </button>
          </div>
        </div>

        {!collapsed && (
          <div style={{ padding: "20px" }}>
            <div className="row g-3">
              <div className="col-md-6">
                <FormFieldRow
                  name="PreschoolCode"
                  labelText="Preschool"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                  idPrefix={idPrefix}
                >
                  {(id, name) => (
                    <Select
                      id={id}
                      disabled={viewOnly}
                      {...register(name, { required: "Preschool is required" })}
                      options={preschoolOptions}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-6">
                <FormFieldRow
                  name="ProgramYear"
                  labelText="Program Year"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                  idPrefix={idPrefix}
                >
                  {(id, name) =>
                    viewOnly ? (
                      <Select
                        id={id}
                        disabled
                        {...register(name, {
                          required: "Program Year is required",
                        })}
                        options={coreData.programYearList}
                      />
                    ) : (
                      <ReadOnlySelect
                        id={id}
                        control={control}
                        name={name}
                        rules={{ required: "Program Year is required" }}
                        options={coreData.programYearList}
                      />
                    )
                  }
                </FormFieldRow>
              </div>

              <div className="col-md-6">
                <FormFieldRow
                  name="LocalClassroomId"
                  labelText="ClassroomID"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                  idPrefix={idPrefix}
                >
                  {(id, name) =>
                    viewOnly ? (
                      <TextInput
                        id={id}
                        disabled
                        maxLength={50}
                        {...register(name, {
                          required: "Classroom ID is required",
                        })}
                      />
                    ) : classroomOptions.length > 0 ? (
                      <Controller
                        name={name}
                        control={control}
                        rules={{ required: "Classroom ID is required" }}
                        render={({ field }) => (
                          <SearchComboBox
                            id={id}
                            options={classroomOptions}
                            value={field.value}
                            placeholder="Search by classroom name or ID"
                            noMatchMessage="No matching classrooms found"
                            hasError={!!errors.LocalClassroomId}
                            isRequired
                            ariaDescribedBy={`${id}-description`}
                            onChange={(value) => {
                              setValue("LocalClassroomId", value, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            }}
                          />
                        )}
                      />
                    ) : (
                      <TextInput
                        id={id}
                        disabled={false}
                        maxLength={50}
                        {...register(name, {
                          required: "Classroom ID is required",
                        })}
                      />
                    )
                  }
                </FormFieldRow>
              </div>

              <div className="col-md-6">
                <FormFieldRow
                  name="LocalStaffId"
                  labelText="Staff Local ID"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                  idPrefix={idPrefix}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      disabled={viewOnly}
                      readOnly
                      maxLength={50}
                      {...register(name, { required: "Staff ID is required" })}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}
